import * as ExcelJS from 'exceljs';
import { BaseField } from '../types/geneva-schema';

export interface TemplateGenerationOptions {
  recordTypeName: string;
  fields: BaseField[];
  includeExamples?: boolean;
}

/**
 * Generates an Excel template file with headers based on Geneva fields
 */
export async function generateExcelTemplate({
  recordTypeName,
  fields,
  includeExamples = true,
}: TemplateGenerationOptions): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(`${recordTypeName} Template`);

  // Add headers
  const headers = fields.map(field => field.xmlTag);
  worksheet.addRow(headers);

  // Style the header row
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true, size: 12 };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE6F3FF' }, // Light blue background
  };
  headerRow.border = {
    bottom: { style: 'thick', color: { argb: 'FF2563EB' } },
  };

  // Set column widths and add field information
  fields.forEach((field, index) => {
    const column = worksheet.getColumn(index + 1);
    column.width = Math.max(field.xmlTag.length + 2, 15); // Minimum width of 15

    // Add field metadata as comments
    let comment = `Field: ${field.xmlTag}\n`;
    comment += `Type: ${field.type}\n`;
    comment += `Required: ${field.required ? 'Yes' : 'No'}`;
    
    if (field.description) {
      comment += `\nDescription: ${field.description}`;
    }

    // Add comment to header cell
    const headerCell = worksheet.getCell(1, index + 1);
    headerCell.note = comment;
  });

  // Add example rows if requested
  if (includeExamples) {
    // Add instruction row
    const instructionRow = worksheet.addRow(
      fields.map(() => '← Enter your data here')
    );
    instructionRow.font = { italic: true, color: { argb: 'FF6B7280' } };

    // Add example data row
    const exampleRow = worksheet.addRow(
      fields.map(field => getExampleValue(field))
    );
    exampleRow.font = { color: { argb: 'FF6B7280' } };

    // Add a note about the example rows
    worksheet.addRow([]);
    const noteRow = worksheet.addRow([
      'NOTE: Rows 2-3 are examples. Please delete them and add your actual data starting from row 2.',
    ]);
    noteRow.font = { italic: true, size: 10, color: { argb: 'FFDC2626' } };
    
    // Merge the note across all columns
    worksheet.mergeCells(`A${noteRow.number}:${getColumnLetter(fields.length)}${noteRow.number}`);
  }

  // Freeze the header row
  worksheet.views = [{ state: 'frozen', ySplit: 1 }];

  // Generate and download the file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${recordTypeName}_Template.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Gets an appropriate example value for a field based on its type
 */
function getExampleValue(field: BaseField): string {
  switch (field.type.toLowerCase()) {
    case 'string':
    case 'text':
      if (field.xmlTag.toLowerCase().includes('date')) {
        return '2024-01-15';
      }
      if (field.xmlTag.toLowerCase().includes('id')) {
        return 'EXT-123';
      }
      return 'Example text';
    
    case 'number':
    case 'integer':
    case 'decimal':
      if (field.xmlTag.toLowerCase().includes('quantity')) {
        return '1000';
      }
      if (field.xmlTag.toLowerCase().includes('price')) {
        return '100.50';
      }
      return '0';
    
    case 'boolean':
      return 'true';
    
    case 'date':
      return '2024-01-15';
    
    default:
      return '';
  }
}

/**
 * Converts column index to Excel column letter (A, B, C, ..., AA, AB, etc.)
 */
function getColumnLetter(columnIndex: number): string {
  let result = '';
  while (columnIndex > 0) {
    columnIndex--;
    result = String.fromCharCode(65 + (columnIndex % 26)) + result;
    columnIndex = Math.floor(columnIndex / 26);
  }
  return result;
}